import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Typography, Button, Tag, Statistic,
  Spin, message, Table, Space, Progress, Tooltip, Avatar, Badge
} from 'antd';
import {
  ClockCircleOutlined,
  LoginOutlined,
  LogoutOutlined,
  CoffeeOutlined,
  CalendarOutlined,
  FileDoneOutlined,
  WalletOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  PieChartOutlined,
  BankOutlined,
  FileTextOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
  ThunderboltFilled,
  ScheduleOutlined,
  CompassOutlined,
  UserOutlined,
  RiseOutlined,
  ShopOutlined,
  RightOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import StaffLayout from './StaffLayout';
import api from '../api';

const { Title, Text, Paragraph } = Typography;

const formatShiftDisplay = (shift) => {
  if (!shift) return { name: 'General Shift', time: '09:30 AM – 06:30 PM' };
  let name = shift.name || 'General Shift';
  if (name.toLowerCase() === 'shift') name = 'Day Shift';
  else name = name.charAt(0).toUpperCase() + name.slice(1);

  const formatT = (t) => {
    if (!t) return '';
    const parts = String(t).split(':');
    if (parts.length >= 2) {
      const h = parseInt(parts[0], 10);
      const m = parts[1];
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      return `${String(h12).padStart(2, '0')}:${m} ${ampm}`;
    }
    return t;
  };

  const startFormatted = formatT(shift.startTime);
  const endFormatted = formatT(shift.endTime);
  const time = (startFormatted && endFormatted) ? `${startFormatted} – ${endFormatted}` : '09:30 AM – 06:30 PM';
  return { name, time };
};

const getGreeting = () => {
  const hour = dayjs().hour();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
};

const StaffDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [punching, setPunching] = useState(false);
  const [breaking, setBreaking] = useState(false);

  // Current logged in user info
  const [currentUser, setCurrentUser] = useState(null);

  // Live Clock
  const [currentTime, setCurrentTime] = useState(new Date());

  // Attendance Status
  const [status, setStatus] = useState(null);
  const [assignedShift, setAssignedShift] = useState(null);

  // Live Timer Counters
  const [workingSeconds, setWorkingSeconds] = useState(0);
  const [breakSeconds, setBreakSeconds] = useState(0);

  // Leave Balances
  const [leaveCategories, setLeaveCategories] = useState([]);
  const [totalLeaveBalance, setTotalLeaveBalance] = useState(0);

  // Staff Dashboard Summary (approvals, advances, expenses, tasks)
  const [summaryData, setSummaryData] = useState(null);

  // Chart Data: Weekly Hours & Monthly Attendance Ratio
  const [weeklyChartData, setWeeklyChartData] = useState([]);
  const [attendanceRatioData, setAttendanceRatioData] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState({ present: 0, totalPayable: 0, percentage: 0 });

  useEffect(() => {
    try {
      const uStr = sessionStorage.getItem('impersonate_user') || localStorage.getItem('user');
      if (uStr) setCurrentUser(JSON.parse(uStr));
    } catch (_) {}
  }, []);

  // Fetch status, summary, and monthly history for graphs
  const loadDashboardData = async () => {
    try {
      const currentMonthKey = dayjs().format('YYYY-MM');
      const [attRes, sumRes, leaveRes, historyRes] = await Promise.all([
        api.get('/attendance/status').catch(() => ({ data: null })),
        api.get('/me/staff-dashboard').catch(() => ({ data: null })),
        api.get('/leave/categories').catch(() => ({ data: null })),
        api.get(`/attendance/history?month=${currentMonthKey}`).catch(() => ({ data: null }))
      ]);

      if (attRes.data?.success && attRes.data?.status) {
        const s = attRes.data.status;
        setStatus(s);
        setAssignedShift(s.assignedShift || null);
        setWorkingSeconds(Number(s.workingSeconds || s.totalDurationSeconds || 0));
        setBreakSeconds(Number(s.breakSeconds || 0));
      }

      if (sumRes.data?.success) {
        setSummaryData(sumRes.data);
      }

      if (leaveRes.data?.success && Array.isArray(leaveRes.data?.categories)) {
        const cats = leaveRes.data.categories;
        setLeaveCategories(cats);
        const remaining = cats
          .filter(c => c.total !== null && !c.unlimited)
          .reduce((sum, c) => sum + (Number(c.remaining) || 0), 0);
        setTotalLeaveBalance(remaining);
      }

      // Process graph data from history
      if (historyRes.data?.success) {
        const days = historyRes.data.days || [];
        const summary = historyRes.data.summary || {};

        // 1. Weekly Chart Data (Current Week: Mon - Sun)
        const today = dayjs();
        const startOfWeek = today.startOf('week'); // Sunday
        const weekDays = [];

        for (let i = 0; i < 7; i++) {
          const currentDay = startOfWeek.add(i, 'day');
          const dateStr = currentDay.format('YYYY-MM-DD');
          const dayMatch = days.find(d => d.date === dateStr);

          const totalWorkHrs = dayMatch?.totalDurationSeconds
            ? Number((dayMatch.totalDurationSeconds / 3600).toFixed(1))
            : (dayMatch?.totalWorkHours ? Number(dayMatch.totalWorkHours) : 0);

          const otHrs = dayMatch?.overtimeSeconds
            ? Number((dayMatch.overtimeSeconds / 3600).toFixed(1))
            : (dayMatch?.overtimeMinutes ? Number((dayMatch.overtimeMinutes / 60).toFixed(1)) : 0);

          weekDays.push({
            day: currentDay.format('ddd'),
            date: currentDay.format('DD MMM'),
            workHours: totalWorkHrs,
            overtimeHours: otHrs,
            status: dayMatch?.dayStatus || (currentDay.isAfter(today, 'day') ? 'UPCOMING' : 'ABSENT'),
            isToday: dateStr === today.format('YYYY-MM-DD')
          });
        }
        setWeeklyChartData(weekDays);

        // 2. Monthly Attendance Ratio (Donut Chart)
        const presentCount = Number(summary.present || 0);
        const halfDayCount = Number(summary.halfDay || 0);
        const leaveCount = Number(summary.leave || 0);
        const weeklyOffCount = Number(summary.weeklyOff || 0);
        const holidayCount = Number(summary.holiday || 0);
        const absentCount = Number(summary.absent || 0);

        const payable = presentCount + (halfDayCount * 0.5) + leaveCount + weeklyOffCount + holidayCount;
        const totalPassedDays = Math.max(1, presentCount + halfDayCount + leaveCount + weeklyOffCount + holidayCount + absentCount);
        const pct = Math.round((payable / totalPassedDays) * 100);

        setMonthlyStats({
          present: presentCount,
          totalPayable: payable,
          percentage: Math.min(100, Math.max(0, pct))
        });

        const ratioItems = [
          { name: 'Present', value: presentCount, color: '#10b981' },
          { name: 'Half Day', value: halfDayCount, color: '#f59e0b' },
          { name: 'Leave', value: leaveCount, color: '#eab308' },
          { name: 'Weekly Off', value: weeklyOffCount, color: '#3b82f6' },
          { name: 'Holiday', value: holidayCount, color: '#8b5cf6' },
          { name: 'Absent', value: absentCount, color: '#ef4444' }
        ].filter(x => x.value > 0);

        setAttendanceRatioData(ratioItems.length > 0 ? ratioItems : [
          { name: 'No Data Yet', value: 1, color: '#cbd5e1' }
        ]);
      }
    } catch (err) {
      console.error('Failed to load staff dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000); // 30s background sync
    return () => clearInterval(interval);
  }, []);

  // 1-second live clock ticker
  useEffect(() => {
    const clockInterval = setInterval(() => {
      setCurrentTime(new Date());

      if (status?.punchedInAt && !status?.punchedOutAt) {
        if (status?.isOnBreak) {
          setBreakSeconds(prev => prev + 1);
        } else {
          setWorkingSeconds(prev => prev + 1);
        }
      }
    }, 1000);

    return () => clearInterval(clockInterval);
  }, [status]);

  // Format seconds into HHh : MMm : SSs
  const formatHMS = (totalSeconds) => {
    const safe = Math.max(0, Math.floor(Number(totalSeconds || 0)));
    const h = Math.floor(safe / 3600);
    const m = Math.floor((safe % 3600) / 60);
    const s = safe % 60;
    return `${String(h).padStart(2, '0')}h : ${String(m).padStart(2, '0')}m : ${String(s).padStart(2, '0')}s`;
  };

  // Get current browser GPS location
  const getCurrentPosition = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        return resolve({ latitude: null, longitude: null, accuracy: null });
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          });
        },
        (err) => {
          console.warn('Geolocation error:', err);
          resolve({ latitude: null, longitude: null, accuracy: null });
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
      );
    });
  };

  // Handle Punch In
  const handlePunchIn = async () => {
    if (punching) return;
    setPunching(true);
    try {
      const pos = await getCurrentPosition();
      const payload = {
        latitude: pos.latitude,
        longitude: pos.longitude,
        clientTime: new Date().toISOString()
      };

      const res = await api.post('/attendance/punch-in', payload);
      if (res.data?.success) {
        message.success('Punched In Successfully!');
        await loadDashboardData();
      } else {
        message.error(res.data?.message || 'Punch in failed');
      }
    } catch (err) {
      console.error('Punch in error:', err);
      const msg = err.response?.data?.message || 'Failed to punch in';
      message.error(msg);
    } finally {
      setPunching(false);
    }
  };

  // Handle Punch Out
  const handlePunchOut = async () => {
    if (punching) return;
    setPunching(true);
    try {
      const pos = await getCurrentPosition();
      const payload = {
        latitude: pos.latitude,
        longitude: pos.longitude,
        clientTime: new Date().toISOString()
      };

      const res = await api.post('/attendance/punch-out', payload);
      if (res.data?.success) {
        message.success('Punched Out Successfully!');
        await loadDashboardData();
      } else {
        message.error(res.data?.message || 'Punch out failed');
      }
    } catch (err) {
      console.error('Punch out error:', err);
      const msg = err.response?.data?.message || 'Failed to punch out';
      message.error(msg);
    } finally {
      setPunching(false);
    }
  };

  // Handle Start / End Break
  const handleToggleBreak = async () => {
    if (breaking) return;
    setBreaking(true);
    try {
      if (status?.isOnBreak) {
        const res = await api.post('/attendance/end-break');
        if (res.data?.success) {
          message.success('Break ended');
          await loadDashboardData();
        }
      } else {
        const res = await api.post('/attendance/start-break');
        if (res.data?.success) {
          message.success('Break started');
          await loadDashboardData();
        }
      }
    } catch (err) {
      console.error('Break error:', err);
      message.error('Failed to update break status');
    } finally {
      setBreaking(false);
    }
  };

  if (loading) {
    return (
      <StaffLayout>
        <div style={{ textAlign: 'center', padding: '120px 0' }}>
          <Spin size="large" tip="Loading Staff Dashboard..." />
        </div>
      </StaffLayout>
    );
  }

  const isPunchedIn = !!status?.punchedInAt;
  const isPunchedOut = !!status?.punchedOutAt;
  const isOnBreak = !!status?.isOnBreak;

  const dayStatus = status?.dayStatus || (isPunchedIn ? 'PRESENT' : 'ABSENT');

  const shiftInfo = formatShiftDisplay(assignedShift);

  // Target 8-hour shift calculation
  const targetWorkSeconds = 8 * 3600;
  const workProgressPct = Math.min(100, Math.round((workingSeconds / targetWorkSeconds) * 100));

  return (
    <StaffLayout>
      {/* 0. WELCOME GREETING & QUICK ACTIONS BAR */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 20,
        padding: '4px 0'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Title level={3} style={{ margin: 0, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em' }}>
              {getGreeting()}, {currentUser?.name || currentUser?.username || 'Staff'} 👋
            </Title>
            <Tag color="blue" style={{ borderRadius: 12, fontWeight: 600, padding: '1px 10px', fontSize: 12 }}>
              ID: {currentUser?.username || currentUser?.id || 'STAFF'}
            </Tag>
          </div>
          <Text type="secondary" style={{ fontSize: 13, marginTop: 4, display: 'inline-block' }}>
            {currentTime.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} • Welcome to your daily workspace
          </Text>
        </div>

        {/* Quick Action Pills */}
        <Space size="middle" wrap>
          <Button
            type="default"
            icon={<CoffeeOutlined style={{ color: '#2563eb' }} />}
            onClick={() => navigate('/staff/leave')}
            style={{ borderRadius: 8, fontWeight: 500, borderColor: '#cbd5e1', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)' }}
          >
            Request Leave
          </Button>
          <Button
            type="default"
            icon={<BankOutlined style={{ color: '#059669' }} />}
            onClick={() => navigate('/staff/advances')}
            style={{ borderRadius: 8, fontWeight: 500, borderColor: '#cbd5e1', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)' }}
          >
            Apply Advance
          </Button>
          <Button
            type="default"
            icon={<FileTextOutlined style={{ color: '#7c3aed' }} />}
            onClick={() => navigate('/staff/expenses')}
            style={{ borderRadius: 8, fontWeight: 500, borderColor: '#cbd5e1', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)' }}
          >
            Submit Expense
          </Button>
          <Button
            type="default"
            icon={<ShopOutlined style={{ color: '#059669' }} />}
            onClick={() => navigate('/staff/sales')}
            style={{ borderRadius: 8, fontWeight: 500, borderColor: '#cbd5e1', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)' }}
          >
            Field Sales
          </Button>
          <Button
            type="primary"
            icon={<ScheduleOutlined />}
            onClick={() => navigate('/staff/roster')}
            style={{ borderRadius: 8, fontWeight: 500, background: '#2563eb', borderColor: '#2563eb' }}
          >
            Shift Roster
          </Button>
        </Space>
      </div>

      {/* 1. TOP HERO CARD: Digital Attendance, Realtime Clock & Smart Punch Pod */}
      <Card
        style={{
          borderRadius: 20,
          background: 'linear-gradient(135deg, #090d16 0%, #0f172a 45%, #172554 100%)',
          color: '#fff',
          boxShadow: '0 20px 30px -10px rgba(15, 23, 42, 0.4), 0 4px 6px -2px rgba(0,0,0,0.1)',
          marginBottom: 24,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          position: 'relative',
          overflow: 'hidden'
        }}
        bodyStyle={{ padding: '26px 30px' }}
      >
        {/* Soft background ambient light spots */}
        <div style={{
          position: 'absolute',
          top: -80,
          right: -80,
          width: 260,
          height: 260,
          borderRadius: '50%',
          background: isPunchedIn && !isPunchedOut ? 'rgba(56, 189, 248, 0.18)' : 'rgba(16, 185, 129, 0.16)',
          filter: 'blur(60px)',
          pointerEvents: 'none'
        }} />
        <div style={{
          position: 'absolute',
          bottom: -70,
          left: 100,
          width: 220,
          height: 220,
          borderRadius: '50%',
          background: 'rgba(99, 102, 241, 0.12)',
          filter: 'blur(55px)',
          pointerEvents: 'none'
        }} />

        <Row gutter={[24, 24]} align="stretch">
          {/* Left: Live Realtime Clock & Assigned Shift Schedule */}
          <Col xs={24} lg={8}>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    background: '#10b981',
                    boxShadow: '0 0 12px #10b981'
                  }} />
                  <span style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase' }}>
                    Live Realtime Clock
                  </span>
                </div>

                <div style={{
                  fontSize: 36,
                  fontWeight: 700,
                  color: '#f8fafc',
                  letterSpacing: 1.5,
                  fontFamily: 'monospace',
                  textShadow: '0 0 25px rgba(56, 189, 248, 0.35)',
                  lineHeight: 1.1
                }}>
                  {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </div>

                <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 6, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CalendarOutlined style={{ color: '#38bdf8' }} />
                  {currentTime.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
              </div>

              {/* Polished Assigned Shift Pod */}
              <div
                onClick={() => navigate('/staff/roster')}
                style={{
                  marginTop: 20,
                  padding: '14px 16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: 14,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(8px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                title="Click to view your shift roster"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: 'rgba(56, 189, 248, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#38bdf8'
                  }}>
                    <ClockCircleOutlined style={{ fontSize: 20 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Assigned Shift
                    </div>
                    <div style={{ fontWeight: 600, color: '#f8fafc', fontSize: 14 }}>
                      {shiftInfo.name}
                      <span style={{ color: '#38bdf8', fontWeight: 600, marginLeft: 8, fontSize: 13 }}>
                        {shiftInfo.time}
                      </span>
                    </div>
                  </div>
                </div>
                <RightOutlined style={{ color: '#64748b', fontSize: 12 }} />
              </div>
            </div>
          </Col>

          {/* Middle: Daily Working Hours & Shift Goal Progress */}
          <Col xs={24} lg={8}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.04)',
              borderRadius: 16,
              padding: '22px 24px',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              backdropFilter: 'blur(10px)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              textAlign: 'center'
            }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <Tag
                    color={
                      dayStatus === 'PRESENT' ? 'success' :
                      (dayStatus === 'HALF_DAY' ? 'warning' : 'error')
                    }
                    style={{
                      fontSize: 12,
                      padding: '3px 14px',
                      borderRadius: 20,
                      fontWeight: 700,
                      border: 0,
                      letterSpacing: 0.5
                    }}
                  >
                    STATUS: {dayStatus}
                  </Tag>
                  {status?.isLate && (
                    <Tag color="volcano" style={{ fontSize: 11, borderRadius: 20, fontWeight: 600 }}>LATE</Tag>
                  )}
                </div>

                <Text style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Working Hours Today
                </Text>

                <div style={{
                  fontSize: 34,
                  fontWeight: 700,
                  color: isPunchedIn && !isPunchedOut ? '#38bdf8' : '#f8fafc',
                  fontFamily: 'monospace',
                  letterSpacing: 1.5,
                  margin: '8px 0 6px 0',
                  textShadow: isPunchedIn && !isPunchedOut ? '0 0 20px rgba(56, 189, 248, 0.4)' : 'none'
                }}>
                  {formatHMS(workingSeconds)}
                </div>

                {/* Progress toward 8-hour target */}
                <div style={{ marginTop: 10, padding: '0 8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
                    <span>Daily Shift Goal</span>
                    <span style={{ color: '#38bdf8', fontWeight: 600 }}>{workProgressPct}% (8 hrs)</span>
                  </div>
                  <Progress
                    percent={workProgressPct}
                    strokeColor={{ '0%': '#38bdf8', '100%': '#10b981' }}
                    trailColor="rgba(255, 255, 255, 0.1)"
                    showInfo={false}
                    strokeWidth={6}
                  />
                </div>
              </div>

              {/* Sub-metrics: Break Time Counter */}
              <div style={{
                marginTop: 14,
                paddingTop: 12,
                borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                background: 'rgba(255, 255, 255, 0.02)',
                borderRadius: 8,
                padding: '8px 12px'
              }}>
                <div style={{ color: '#94a3b8', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <CoffeeOutlined style={{ color: '#f59e0b' }} />
                  <span>Break Elapsed:</span>
                  <strong style={{ color: '#fcd34d', fontFamily: 'monospace', fontSize: 13 }}>
                    {formatHMS(breakSeconds)}
                  </strong>
                </div>
              </div>
            </div>
          </Col>

          {/* Right: Executive Smart Punch Pod */}
          <Col xs={24} lg={8}>
            <div style={{
              background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.85) 0%, rgba(15, 23, 42, 0.98) 100%)',
              borderRadius: 16,
              padding: '22px 24px',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxShadow: '0 12px 28px 0 rgba(0, 0, 0, 0.35)',
              position: 'relative'
            }}>
              {/* Pod Top Bar with active pulse */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  color: isPunchedIn && !isPunchedOut ? '#38bdf8' : (isPunchedOut ? '#10b981' : '#94a3b8'),
                  background: 'rgba(255, 255, 255, 0.06)',
                  padding: '4px 12px',
                  borderRadius: 20
                }}>
                  <span style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: isPunchedIn && !isPunchedOut ? '#38bdf8' : (isPunchedOut ? '#10b981' : '#10b981'),
                    boxShadow: isPunchedIn && !isPunchedOut ? '0 0 10px #38bdf8' : '0 0 10px #10b981'
                  }} />
                  {isPunchedIn && !isPunchedOut
                    ? (isOnBreak ? 'On Break' : 'Currently Active')
                    : (isPunchedOut ? 'Shift Completed' : 'Ready to Punch In')}
                </span>

                <span style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <CompassOutlined style={{ color: '#10b981' }} /> GPS Active
                </span>
              </div>

              {/* Main Punch Action Button */}
              <div>
                {!isPunchedIn ? (
                  <Button
                    type="primary"
                    size="large"
                    icon={<LoginOutlined style={{ fontSize: 19 }} />}
                    loading={punching}
                    onClick={handlePunchIn}
                    style={{
                      width: '100%',
                      height: 56,
                      borderRadius: 14,
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      borderColor: '#10b981',
                      fontSize: 16,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      boxShadow: '0 10px 24px -4px rgba(16, 185, 129, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8
                    }}
                  >
                    Punch In Now
                  </Button>
                ) : !isPunchedOut ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <Button
                      type="primary"
                      danger
                      size="large"
                      icon={<LogoutOutlined style={{ fontSize: 17 }} />}
                      loading={punching}
                      onClick={handlePunchOut}
                      style={{
                        width: '100%',
                        height: 48,
                        borderRadius: 12,
                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        fontSize: 15,
                        fontWeight: 700,
                        boxShadow: '0 8px 20px -4px rgba(239, 68, 68, 0.45)'
                      }}
                    >
                      Punch Out
                    </Button>

                    <Button
                      ghost
                      size="middle"
                      icon={<CoffeeOutlined />}
                      loading={breaking}
                      onClick={handleToggleBreak}
                      style={{
                        width: '100%',
                        borderRadius: 10,
                        borderColor: isOnBreak ? '#f59e0b' : 'rgba(255, 255, 255, 0.25)',
                        background: isOnBreak ? 'rgba(245, 158, 11, 0.15)' : 'rgba(255, 255, 255, 0.04)',
                        color: isOnBreak ? '#f59e0b' : '#f1f5f9',
                        height: 38,
                        fontWeight: 600,
                        fontSize: 13
                      }}
                    >
                      {isOnBreak ? 'End Break Session' : 'Take a Break'}
                    </Button>
                  </div>
                ) : (
                  <div style={{
                    padding: '16px',
                    background: 'rgba(16, 185, 129, 0.12)',
                    borderRadius: 12,
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    textAlign: 'center'
                  }}>
                    <CheckCircleFilled style={{ fontSize: 26, color: '#10b981', marginBottom: 6 }} />
                    <div style={{ fontWeight: 700, color: '#10b981', fontSize: 15 }}>Day Shift Completed</div>
                    <Text style={{ color: '#94a3b8', fontSize: 12 }}>Punch-out recorded & verified successfully.</Text>
                  </div>
                )}

                {/* In/Out Timestamps row */}
                {isPunchedIn && (
                  <div style={{
                    fontSize: 12,
                    color: '#94a3b8',
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: 12,
                    background: 'rgba(0,0,0,0.25)',
                    padding: '8px 14px',
                    borderRadius: 8
                  }}>
                    <span>In: <strong style={{ color: '#fff' }}>{dayjs(status.punchedInAt).format('hh:mm A')}</strong></span>
                    {status.punchedOutAt && (
                      <span>Out: <strong style={{ color: '#fff' }}>{dayjs(status.punchedOutAt).format('hh:mm A')}</strong></span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 2. STATS ROW: 4 High-Polish Metric Cards */}
      <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
        {/* Card 1: Leave Balance */}
        <Col xs={24} sm={12} lg={6}>
          <Card
            hoverable
            style={{
              borderRadius: 16,
              height: '100%',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.04)',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            bodyStyle={{ padding: '20px 22px' }}
            onClick={() => navigate('/staff/leave')}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontWeight: 600, color: '#64748b', fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Leave Balance
              </span>
              <div style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: '#eff6ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#2563eb'
              }}>
                <CalendarOutlined style={{ fontSize: 18 }} />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 28, fontWeight: 700, color: '#1e293b', lineHeight: 1 }}>
                {totalLeaveBalance}
              </span>
              <span style={{ color: '#64748b', fontWeight: 500, fontSize: 14 }}>days available</span>
            </div>

            <div style={{ marginTop: 14, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {leaveCategories.slice(0, 3).map((c, i) => (
                <Tag key={i} color="blue" style={{ fontSize: 11, borderRadius: 6, border: 0, background: '#f1f5f9', color: '#334155' }}>
                  {c.name}: <strong>{c.remaining ?? '∞'}</strong>
                </Tag>
              ))}
              {leaveCategories.length === 0 && (
                <Tag color="default" style={{ fontSize: 11, borderRadius: 6 }}>No quota allocated</Tag>
              )}
            </div>

            <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#2563eb', fontWeight: 600 }}>Apply for Leave</span>
              <ArrowRightOutlined style={{ color: '#2563eb', fontSize: 12 }} />
            </div>
          </Card>
        </Col>

        {/* Card 2: Pending Approvals */}
        <Col xs={24} sm={12} lg={6}>
          <Card
            hoverable
            style={{
              borderRadius: 16,
              height: '100%',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.04)',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            bodyStyle={{ padding: '20px 22px' }}
            onClick={() => navigate('/staff/attendance')}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontWeight: 600, color: '#64748b', fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Pending Requests
              </span>
              <div style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: '#fffbeb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#d97706'
              }}>
                <FileDoneOutlined style={{ fontSize: 18 }} />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 28, fontWeight: 700, color: '#1e293b', lineHeight: 1 }}>
                {summaryData?.pendingApprovals?.total || 0}
              </span>
              <span style={{ color: '#d97706', fontWeight: 600, fontSize: 13 }}>awaiting review</span>
            </div>

            <div style={{ marginTop: 14, fontSize: 12, color: '#64748b' }}>
              <span>Leaves: <strong style={{ color: '#0f172a' }}>{summaryData?.pendingApprovals?.leaves || 0}</strong></span> • 
              <span style={{ marginLeft: 6 }}>Regularization: <strong style={{ color: '#0f172a' }}>{summaryData?.pendingApprovals?.regularizations || 0}</strong></span>
            </div>

            <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#d97706', fontWeight: 600 }}>Track Approvals</span>
              <ArrowRightOutlined style={{ color: '#d97706', fontSize: 12 }} />
            </div>
          </Card>
        </Col>

        {/* Card 3: Outstanding Advance */}
        <Col xs={24} sm={12} lg={6}>
          <Card
            hoverable
            style={{
              borderRadius: 16,
              height: '100%',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.04)',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            bodyStyle={{ padding: '20px 22px' }}
            onClick={() => navigate('/staff/advances')}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontWeight: 600, color: '#64748b', fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Outstanding Advance
              </span>
              <div style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: '#ecfdf5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#059669'
              }}>
                <BankOutlined style={{ fontSize: 18 }} />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 26, fontWeight: 700, color: '#059669', lineHeight: 1 }}>
                ₹{Number(summaryData?.advance?.balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            </div>

            <div style={{ marginTop: 14 }}>
              <Tag color="cyan" style={{ borderRadius: 6, border: 0, fontSize: 11, padding: '2px 8px' }}>
                Salary EMI Deductions Linked
              </Tag>
            </div>

            <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>Apply & View Advances</span>
              <ArrowRightOutlined style={{ color: '#059669', fontSize: 12 }} />
            </div>
          </Card>
        </Col>

        {/* Card 4: Expense Claims */}
        <Col xs={24} sm={12} lg={6}>
          <Card
            hoverable
            style={{
              borderRadius: 16,
              height: '100%',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.04)',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            bodyStyle={{ padding: '20px 22px' }}
            onClick={() => navigate('/staff/expenses')}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontWeight: 600, color: '#64748b', fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Expense Claims
              </span>
              <div style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: '#f5f3ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#7c3aed'
              }}>
                <FileTextOutlined style={{ fontSize: 18 }} />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 28, fontWeight: 700, color: '#1e293b', lineHeight: 1 }}>
                {summaryData?.expenses?.recent?.length || 0}
              </span>
              <span style={{ color: '#64748b', fontWeight: 500, fontSize: 14 }}>recent submissions</span>
            </div>

            <div style={{ marginTop: 14 }}>
              <Tag color="purple" style={{ borderRadius: 6, border: 0, fontSize: 11, padding: '2px 8px' }}>
                Travel, Food, Fuel & Bills
              </Tag>
            </div>

            <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#7c3aed', fontWeight: 600 }}>Open Expense Portal</span>
              <ArrowRightOutlined style={{ color: '#7c3aed', fontSize: 12 }} />
            </div>
          </Card>
        </Col>
      </Row>

      {/* 3. VISUAL ANALYTICS & TRENDS (Charts) */}
      <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
        {/* Weekly Working Hours Bar Chart */}
        <Col xs={24} lg={15}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: '#eff6ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#2563eb'
                }}>
                  <BarChartOutlined style={{ fontSize: 16 }} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>Weekly Working Hours & Overtime</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>Daily hours logged against shifts</div>
                </div>
              </div>
            }
            extra={
              <Tag color="blue" style={{ borderRadius: 8, fontWeight: 600, padding: '2px 10px' }}>
                This Week (Sun - Sat)
              </Tag>
            }
            style={{ borderRadius: 18, border: '1px solid #e2e8f0', boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.04)' }}
            bodyStyle={{ padding: '22px 20px 16px 20px' }}
          >
            <ResponsiveContainer width="100%" height={290}>
              <BarChart data={weeklyChartData} margin={{ top: 10, right: 15, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="barWorkGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#1d4ed8" stopOpacity={0.9} />
                  </linearGradient>
                  <linearGradient id="barOtGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#059669" stopOpacity={0.9} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis
                  dataKey="day"
                  stroke="#94a3b8"
                  fontSize={12}
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                />
                <YAxis
                  stroke="#94a3b8"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  unit="h"
                  domain={[0, 'auto']}
                />
                <RechartsTooltip
                  cursor={{ fill: 'rgba(241, 245, 249, 0.7)' }}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div style={{
                          background: '#0f172a',
                          padding: '12px 16px',
                          borderRadius: 12,
                          color: '#fff',
                          boxShadow: '0 12px 20px -3px rgba(0,0,0,0.4)',
                          fontSize: 12
                        }}>
                          <div style={{ fontWeight: 700, color: '#f8fafc', marginBottom: 6, fontSize: 13 }}>
                            {label} ({data.date})
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#38bdf8', marginBottom: 2 }}>
                            <span>● Regular Hours:</span>
                            <strong>{data.workHours} hrs</strong>
                          </div>
                          {Number(data.overtimeHours) > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#4ade80', marginBottom: 2 }}>
                              <span>● Overtime:</span>
                              <strong>+{data.overtimeHours} hrs</strong>
                            </div>
                          )}
                          <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                            <Tag color="cyan" style={{ fontSize: 10, padding: '0 6px', margin: 0, borderRadius: 4 }}>
                              Status: {data.status}
                            </Tag>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend
                  verticalAlign="top"
                  align="right"
                  iconType="circle"
                  wrapperStyle={{ paddingBottom: 12, fontSize: 12, fontWeight: 500 }}
                />
                <Bar
                  dataKey="workHours"
                  name="Regular Hours"
                  fill="url(#barWorkGradient)"
                  radius={[6, 6, 0, 0]}
                  barSize={24}
                />
                <Bar
                  dataKey="overtimeHours"
                  name="Overtime Hours"
                  fill="url(#barOtGradient)"
                  radius={[6, 6, 0, 0]}
                  barSize={24}
                />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Monthly Attendance Ratio Donut Chart */}
        <Col xs={24} lg={9}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: '#ecfdf5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#059669'
                }}>
                  <PieChartOutlined style={{ fontSize: 16 }} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>Monthly Attendance Ratio</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>Summary for {dayjs().format('MMMM YYYY')}</div>
                </div>
              </div>
            }
            extra={
              <Tag color="green" style={{ borderRadius: 8, fontWeight: 600, padding: '2px 10px' }}>
                {monthlyStats.percentage}% Ratio
              </Tag>
            }
            style={{ borderRadius: 18, border: '1px solid #e2e8f0', boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.04)' }}
            bodyStyle={{ padding: '20px 20px' }}
          >
            <div style={{ position: 'relative', width: '100%', height: 180 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={attendanceRatioData}
                    cx="50%"
                    cy="50%"
                    innerRadius={56}
                    outerRadius={82}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {attendanceRatioData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const item = payload[0];
                        return (
                          <div style={{
                            background: '#0f172a',
                            color: '#fff',
                            padding: '6px 12px',
                            borderRadius: 8,
                            fontSize: 12
                          }}>
                            <strong>{item.name}:</strong> {item.value} days
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>

              {/* Center Donut Statistic */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                pointerEvents: 'none'
              }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
                  {monthlyStats.percentage}%
                </div>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 2, fontWeight: 600, textTransform: 'uppercase' }}>
                  Payable
                </div>
              </div>
            </div>

            {/* Attendance Breakdown Pills */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 8,
              marginTop: 14,
              paddingTop: 14,
              borderTop: '1px solid #f1f5f9'
            }}>
              {attendanceRatioData.map((item, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 11,
                  background: '#f8fafc',
                  padding: '5px 8px',
                  borderRadius: 6,
                  border: '1px solid #f1f5f9'
                }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.color }} />
                  <span style={{ color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.name}: <strong>{item.value}</strong>
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      {/* 4. LOWER SECTION: Recent Tasks & Expenses */}
      <Row gutter={[20, 20]}>
        {/* Recent Tasks */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircleOutlined style={{ color: '#2563eb', fontSize: 16 }} />
                <span style={{ fontWeight: 600 }}>My Tasks & Assigned Work</span>
              </div>
            }
            extra={
              <Space>
                <Tag color="blue" style={{ borderRadius: 6 }}>{summaryData?.tasks?.recent?.length || 0} Recent</Tag>
                <Button type="link" size="small" onClick={() => navigate('/staff/tasks')} style={{ padding: 0, fontWeight: 600 }}>
                  View All →
                </Button>
              </Space>
            }
            style={{ borderRadius: 16, border: '1px solid #e2e8f0', height: '100%', boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.04)' }}
          >
            {(!summaryData?.tasks?.recent || summaryData.tasks.recent.length === 0) ? (
              <div style={{ textAlign: 'center', padding: '36px 0', color: '#94a3b8' }}>
                <CheckCircleOutlined style={{ fontSize: 34, marginBottom: 8, color: '#cbd5e1' }} />
                <div style={{ fontWeight: 500 }}>No pending tasks assigned</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>You're all caught up for today!</div>
              </div>
            ) : (
              <Table
                dataSource={summaryData.tasks.recent}
                rowKey="id"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: 'Title',
                    dataIndex: 'title',
                    key: 'title',
                    render: (t) => <Text strong>{t || 'Task'}</Text>
                  },
                  {
                    title: 'Date',
                    dataIndex: 'date',
                    key: 'date',
                    render: (d) => d ? new Date(d).toLocaleDateString() : '--'
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    key: 'status',
                    render: (s) => (
                      <Tag color={s === 'COMPLETED' ? 'green' : 'orange'}>
                        {s || 'PENDING'}
                      </Tag>
                    )
                  }
                ]}
              />
            )}
          </Card>
        </Col>

        {/* Recent Expenses */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileTextOutlined style={{ color: '#7c3aed', fontSize: 16 }} />
                <span style={{ fontWeight: 600 }}>Recent Expense Claims</span>
              </div>
            }
            extra={
              <Space>
                <Tag color="purple" style={{ borderRadius: 6 }}>{summaryData?.expenses?.recent?.length || 0} Recent</Tag>
                <Button
                  type="link"
                  size="small"
                  onClick={() => navigate('/staff/expenses')}
                  style={{ padding: 0, fontWeight: 600, color: '#7c3aed' }}
                >
                  View All →
                </Button>
              </Space>
            }
            style={{ borderRadius: 16, border: '1px solid #e2e8f0', height: '100%', boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.04)' }}
          >
            {(!summaryData?.expenses?.recent || summaryData.expenses.recent.length === 0) ? (
              <div style={{ textAlign: 'center', padding: '36px 0', color: '#94a3b8' }}>
                <WalletOutlined style={{ fontSize: 34, marginBottom: 8, color: '#cbd5e1' }} />
                <div style={{ fontWeight: 500 }}>No expense claims found</div>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => navigate('/staff/expenses')}
                  style={{ marginTop: 12, borderRadius: 6, background: '#7c3aed', borderColor: '#7c3aed' }}
                >
                  Submit Expense Claim
                </Button>
              </div>
            ) : (
              <div>
                <Table
                  dataSource={summaryData.expenses.recent}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  onRow={() => ({
                    onClick: () => navigate('/staff/expenses'),
                    style: { cursor: 'pointer' }
                  })}
                  columns={[
                    {
                      title: 'Category',
                      dataIndex: 'category',
                      key: 'category',
                      render: (c) => <Text strong>{c || 'Expense'}</Text>
                    },
                    {
                      title: 'Amount',
                      dataIndex: 'amount',
                      key: 'amount',
                      render: (a) => `₹${Number(a || 0).toLocaleString()}`
                    },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      key: 'status',
                      render: (s) => {
                        const col = s === 'approved' ? 'green' : (s === 'rejected' ? 'red' : 'gold');
                        return <Tag color={col}>{(s || 'pending').toUpperCase()}</Tag>;
                      }
                    }
                  ]}
                />
                <div style={{ marginTop: 12, textAlign: 'right' }}>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => navigate('/staff/expenses')}
                    style={{ fontWeight: 600, color: '#7c3aed' }}
                  >
                    Open Expenses Portal →
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </StaffLayout>
  );
};

export default StaffDashboard;
